// 微信小程序 webview 跨端登录同步工具
import { saveLoginSession, saveHasInvite, USER_ID_KEY } from "./auth";

let wxMiniBridgePromise = null;
// 记录上次同步的 URL 参数字符串，避免重复同步相同参数
let lastSyncedSearchStr = "";
// 记录上次同步的“登录时间戳”（小程序每次登录跳转都会带上新的 loginAt）
// 用于识别“新登录”，同一账号重复登录时 userId 不变也能判断出来
const LOGIN_AT_KEY = "medicalLoginAt";

/**
 * 解析 URL 中与小程序登录相关的参数
 */
export const parseWxMiniAuthParams = () => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search || "");
    if (params.get("source") !== "wxmini") return null;

    return {
        loginStatus: params.get("loginStatus"),
        nextUserId: params.get("medicalUserId") || params.get("userId"),
        nextPhone: params.get("medicalUserPhone") || params.get("phone") || "",
        // 试用信息（小程序侧登录接口返回后随 URL 透传，H5 登录成功弹窗逻辑复用）
        nextNewUserFreeTrial: params.get("newUserFreeTrial"),
        nextFreeTrialDays: params.get("freeTrialDays"),
        // 邀请资格（小程序侧登录接口返回后随 URL 透传，未携带时按无资格处理）
        nextHasInvite: params.get("hasInvite"),
        // 本次登录时间戳（小程序侧每次登录都会带上，用于识别“新登录”）
        nextLoginAt: params.get("loginAt"),
    };
};

/**
 * 从 URL 参数同步微信小程序的登录态
 * 小程序打开 H5 时通过 URL 传参（source=wxmini），H5 据此同步登录/游客状态
 *
 * 支持重复调用（如小程序一键登录后重新加载 WebView），自动跳过已同步的相同参数
 *
 * @returns {object|null} 同步结果，包含 userId/phone/loginStatus 或 null（未同步）
 */
export const syncWxMiniAuthFromUrl = () => {
    if (typeof window === "undefined") return null;

    const currentSearch = window.location.search || "";
    // 如果当前 URL 参数与此前同步过的一致，跳过
    if (currentSearch && currentSearch === lastSyncedSearchStr) return null;

    const authParams = parseWxMiniAuthParams();
    console.log(">>> [wxmini sync] search:", currentSearch, "| lastSynced:", lastSyncedSearchStr, "| parsed:", authParams);
    if (!authParams) return null;

    const {
        loginStatus,
        nextUserId,
        nextPhone,
        nextNewUserFreeTrial,
        nextFreeTrialDays,
        nextHasInvite,
        nextLoginAt,
    } = authParams;

    if (loginStatus === "logged_in" && nextUserId) {
        // 判断是否为“新登录”：
        // 优先用 URL 带过来的登录时间戳（同一账号重复登录时 userId 不变，但 loginAt 每次不同）
        // 老版本小程序不带 loginAt，则回退到原先的 userId 比较
        // 用于 H5 在登录成功后展示欢迎弹窗（未成年人提示 + VIP 体验卡），仅新登录触发
        const prevUserId = localStorage.getItem(USER_ID_KEY);
        const prevLoginAt = localStorage.getItem(LOGIN_AT_KEY);
        const isNewLogin = nextLoginAt
            ? prevLoginAt !== nextLoginAt
            : (!prevUserId || prevUserId !== nextUserId);
        console.log(">>> [wxmini sync] logged_in | prevUserId:", prevUserId, "| nextUserId:", nextUserId, "| prevLoginAt:", prevLoginAt, "| nextLoginAt:", nextLoginAt, "| isNewLogin:", isNewLogin);
        saveLoginSession(nextUserId, nextPhone);
        if (nextLoginAt) {
            localStorage.setItem(LOGIN_AT_KEY, nextLoginAt);
        }
        // URL 未携带 hasInvite 时按无邀请资格处理
        saveHasInvite(nextHasInvite === null || nextHasInvite === undefined ? false : nextHasInvite);
        lastSyncedSearchStr = currentSearch;
        return {
            userId: nextUserId,
            phone: nextPhone,
            loginStatus: "logged_in",
            isNewLogin,
            newUserFreeTrial:  Number(nextNewUserFreeTrial),
            freeTrialDays:  Number(nextFreeTrialDays),
            hasInvite: nextHasInvite === "1" || nextHasInvite === "true",
        };
    }

    if (loginStatus === "guest" && nextUserId) {
        localStorage.setItem("medicalUserId", nextUserId);
        localStorage.setItem("loginStatus", "guest");
        localStorage.removeItem("medicalUserPhone");
        localStorage.removeItem("medicalUserExpiry");
        localStorage.removeItem("medicalHasInvite");
        lastSyncedSearchStr = currentSearch;
        return { userId: nextUserId, phone: "", loginStatus: "guest", hasInvite: false };
    }

    return null;
};

/**
 * 重置同步记录，允许下一次 syncWxMiniAuthFromUrl 重新同步
 * 用于小程序一键登录后通过 URL 重载 WebView 重新传递登录态的场景
 */
export const resetWxMiniAuthSync = () => {
    lastSyncedSearchStr = "";
};

/**
 * 懒加载微信 JS-SDK，返回 window.wx.miniProgram 对象
 * 用于在小程序 webview 中与小程序通信
 */
export const loadWxMiniProgramBridge = () => {
    if (typeof window === "undefined") return Promise.reject(new Error("window is unavailable"));
    if (window.wx?.miniProgram) return Promise.resolve(window.wx.miniProgram);
    if (wxMiniBridgePromise) return wxMiniBridgePromise;

    wxMiniBridgePromise = new Promise((resolve, reject) => {
        const existingScript = document.getElementById("wx-jssdk");
        const handleReady = () => {
            if (window.wx?.miniProgram) {
                resolve(window.wx.miniProgram);
            } else {
                reject(new Error("wx.miniProgram is unavailable after loading jssdk"));
            }
        };

        if (existingScript) {
            existingScript.addEventListener("load", handleReady, { once: true });
            existingScript.addEventListener("error", reject, { once: true });
            return;
        }

        const script = document.createElement("script");
        script.id = "wx-jssdk";
        script.src = "https://res.wx.qq.com/open/js/jweixin-1.6.0.js";
        script.async = true;
        script.onload = handleReady;
        script.onerror = () => reject(new Error("failed to load wx jssdk"));
        document.head.appendChild(script);
    });

    return wxMiniBridgePromise;
};

/**
 * H5 退出登录时通知小程序同步登出
 */
export const notifyWxMiniLogout = () => {
    if (typeof window === "undefined") return;

    // const payload = {
    //     source: "h5",
    //     type: "logout",
    //     action: "logout",
    //     loginStatus: "logout",
    //     timestamp: Date.now(),
    // };

    loadWxMiniProgramBridge().catch((err) => {
        console.warn("[wxmini logout sync] failed:", err);
    });
};

/**
 * 小程序原生登录页路径
 * 小程序内所有“跳转登录”的场景均跳转到该页面
 */
export const WX_MINI_LOGIN_PAGE_PATH = "/pages/wechat/WeCahtLogin";

/**
 * 同步判断当前是否运行在微信小程序 webview 中
 */
export const isWxMiniProgramEnv = () => {
    if (typeof window === "undefined") return false;
    return window.__wxjs_environment === "miniprogram";
};

/**
 * 跳转到小程序原生登录页（pages/wechat/WeCahtLogin）
 * 仅在小程序环境且 JS-SDK 就绪时跳转
 *
 * @returns {boolean} 是否已成功发起跳转
 */
export const redirectToWxMiniLogin = () => {
    if(!isWxMiniProgramEnv()) return false
    if (window.wx?.miniProgram) {
        const userId = localStorage.getItem(USER_ID_KEY) || createGuestId();
        const loginUrl =`${WX_MINI_LOGIN_PAGE_PATH}?userId=${encodeURIComponent(userId)}`
        window.wx.miniProgram.navigateTo({
            url: loginUrl,
        });
        return true;
    }
    return false;
};

function createGuestId() {
    return `user_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * 异步跳转到小程序原生登录页，适用于 JS-SDK 尚未就绪的场景（如页面初始化挂载）
 * 非小程序环境会立即返回 false，不影响 H5 内原有登录流程
 *
 * @returns {Promise<boolean>} 是否已成功发起跳转
 */
export const redirectToWxMiniLoginAsync = async () => {
    if (typeof window === "undefined") return false;
    if (redirectToWxMiniLogin()) return true;
    // JS-SDK 尚未就绪：仅当环境标记表明当前在小程序中时才等待 SDK 加载，
    // 避免在普通浏览器中无谓地加载微信 JS-SDK
    if (window.__wxjs_environment !== "miniprogram") return false;
    try {
        await loadWxMiniProgramBridge();
        return redirectToWxMiniLogin();
    } catch (err) {
        console.warn("[wxmini login redirect] failed:", err);
        return false;
    }
};

/**
 * 异步获取微信小程序通信桥接对象 wx.miniProgram
 * 统一处理：非浏览器环境、SDK 已就绪、非小程序环境、SDK 懒加载失败 等场景，
 * 调用方无需关心前置条件，拿不到统一返回 null
 *
 * @returns {Promise<object|null>} wx.miniProgram 对象，不可用时为 null
 */
export const getWxMiniProgramAsync = async () => {
    if (typeof window === "undefined") return null;
    if (window.wx?.miniProgram) return window.wx.miniProgram;
    // 环境闸门：仅当当前在小程序 webview 中才加载微信 JS-SDK，
    // 避免在普通浏览器中无谓地加载 SDK（普通浏览器中 window.wx.miniProgram 永远不存在）
    if (!isWxMiniProgramEnv()) return null;
    try {
        return await loadWxMiniProgramBridge();
    } catch (err) {
        console.warn("[wxmini bridge] failed:", err);
        return null;
    }
};
